/**
 * Obean's beans (ADR 0018): every player is a bean, coloured by their seat in the room, wearing the
 * extra they picked, with a face for what's happening (ready, talking, won, reconnecting).
 *
 * Everything is data, drawn by one function (`beanSvg`) into finished SVG. The web app, the website,
 * the icons and the api (which only checks extra ids) all read this file. To add an extra, add one
 * entry to EXTRAS; to add a mood, one to FACES. A refined bean from the designer (P4-BR-01) replaces
 * the paths here, and `pnpm --filter @obean/web icons` redraws the icons.
 *
 * Every string that reaches the SVG comes from this file's constants or is a number, so the output is
 * safe to put into a page as markup.
 */

/** A bean (shape A, chosen 2026-10-09) in a 120 × 120 box. */
export const BEAN_PATH =
  "M60 22 C84 22 98 40 96 62 C95 76 88 82 90 96 C91 108 78 114 60 114 C38 114 24 100 24 80 C24 64 31 56 29 44 C27 30 40 22 60 22 Z";

/** The colours seats get in a room, in order (the room hands out the lowest free one). */
export const SEAT_COLOURS = ["#5eead4", "#f6c945", "#f79ac0", "#b49cff"] as const;

/** A seat's colour. Rooms may outgrow the palette before the designer adds colours: it wraps. */
export function seatColour(index: number): string {
  const n = SEAT_COLOURS.length;
  const i = Number.isInteger(index) ? ((index % n) + n) % n : 0;
  return SEAT_COLOURS[i] ?? SEAT_COLOURS[0];
}

/** A colour for someone outside a room (a friend in a list): steady for them, from their id. */
export function colourFor(id: string): number {
  let h = 0;
  for (const c of id) h = (h * 31 + (c.codePointAt(0) ?? 0)) >>> 0;
  return h % SEAT_COLOURS.length;
}

const INK = "#0c0f16";

export const MOODS = ["ready", "talking", "won", "reconnecting"] as const;
export type Mood = (typeof MOODS)[number];

const eye = (x: number) => `M${String(x - 3.6)} 64 a3.6 5 0 1 0 7.2 0 a3.6 5 0 1 0 -7.2 0 Z`;
const line = `fill="none" stroke="${INK}" stroke-width="3.5" stroke-linecap="round" stroke-linejoin="round"`;

/** Each mood's face, in the bean's box. */
const FACES: Record<Mood, string> = {
  ready: `<path fill="${INK}" d="${eye(48)} ${eye(72)}"/><path ${line} d="M52 79 Q60 87 68 79"/>`,
  talking: `<path fill="${INK}" d="${eye(48)} ${eye(72)} M54 81 a6 5 0 1 0 12 0 a6 5 0 1 0 -12 0 Z"/>`,
  won: `<path ${line} d="M43 66 Q48 58 53 66 M67 66 Q72 58 77 66"/><path fill="${INK}" stroke="${INK}" stroke-width="2" stroke-linejoin="round" d="M49 77 Q60 95 71 77 Z"/>`,
  reconnecting: `<path ${line} d="M44 65 H52 M68 65 H76 M56 82 H64"/>`,
};

export interface Extra {
  id: string;
  /** Shown in the picker; at most 12 characters. */
  label: string;
  /** Drawn behind the bean (a sprout growing from it) or in front (things it wears). */
  layer: "behind" | "front";
  /** SVG elements in the bean's 120 × 120 box. */
  svg: string;
  /** The level that unlocks it (ADR 0043's beans); open to everyone without one. */
  level?: number;
}

const CORAL = "#ff8f7a";
const CORAL_DARK = "#e46f5b";

/** The extras anyone can pick, in the picker's order. Plain (no extra) comes first, as `null`. */
export const EXTRAS = [
  {
    id: "sprout",
    label: "Sprout",
    layer: "behind",
    svg: `<path fill="none" stroke="#4ade80" stroke-width="3.5" stroke-linecap="round" d="M60 26 C59 18 60 12 63 7"/><path fill="#4ade80" d="M62 11 C68 1 82 2 83 8 C78 15 69 16 62 11 Z"/><path fill="#22c55e" d="M60 15 C53 7 42 8 41 13 C46 20 54 20 60 15 Z"/>`,
  },
  {
    id: "beanie",
    label: "Beanie",
    layer: "front",
    svg: `<path fill="${CORAL}" d="M31 46 C31 24 45 13 60 13 C75 13 89 24 89 46 Z"/><rect x="27" y="40" width="66" height="12" rx="6" fill="${CORAL_DARK}"/><circle cx="60" cy="11" r="7" fill="#ffd1c7"/>`,
  },
  {
    id: "glasses",
    label: "Glasses",
    layer: "front",
    svg: `<g fill="#ffffff" fill-opacity="0.22" stroke="${INK}" stroke-width="3"><circle cx="48" cy="64" r="10"/><circle cx="72" cy="64" r="10"/></g><path fill="none" stroke="${INK}" stroke-width="3" stroke-linecap="round" d="M58 63 Q60 60 62 63"/>`,
  },
  {
    id: "headphones",
    label: "Headphones",
    layer: "front",
    svg: `<path fill="none" stroke="${INK}" stroke-width="9" stroke-linecap="round" d="M22 70 C22 2 98 2 98 70"/><path fill="none" stroke="#edf0f6" stroke-width="6" stroke-linecap="round" d="M22 70 C22 2 98 2 98 70"/><g fill="#edf0f6" stroke="${INK}" stroke-width="2"><rect x="14" y="58" width="16" height="26" rx="7"/><rect x="90" y="58" width="16" height="26" rx="7"/></g>`,
  },
  {
    id: "bow",
    label: "Bow",
    layer: "front",
    svg: `<g fill="${CORAL}" stroke="${CORAL_DARK}" stroke-width="2" stroke-linejoin="round"><path d="M78 27 L92 19 L92 35 Z"/><path d="M78 27 L64 19 L64 35 Z"/></g><circle cx="78" cy="27" r="4.5" fill="${CORAL_DARK}"/>`,
  },
  // Unlocked by level (ADR 0043, #585): something to grow into, never bought.
  {
    id: "flower",
    label: "Flower",
    layer: "front",
    level: 3,
    svg: `<g fill="#fda4af" stroke="${CORAL_DARK}" stroke-width="1.5"><circle cx="78.0" cy="15.0" r="6.5"/><circle cx="86.6" cy="21.2" r="6.5"/><circle cx="83.3" cy="31.3" r="6.5"/><circle cx="72.7" cy="31.3" r="6.5"/><circle cx="69.4" cy="21.2" r="6.5"/></g><circle cx="78" cy="24" r="5" fill="#fde047" stroke="#ca8a04" stroke-width="1.5"/>`,
  },
  {
    id: "party-hat",
    label: "Party hat",
    layer: "front",
    level: 5,
    svg: `<path fill="#a78bfa" stroke="${INK}" stroke-width="2.5" stroke-linejoin="round" d="M44 30 L60 -4 L76 30 Z"/><path fill="none" stroke="#fde047" stroke-width="3" stroke-linecap="round" d="M50 18 L54 21 M57 9 L61 12 M63 22 L67 25"/><circle cx="60" cy="-5" r="5" fill="#fde047" stroke="${INK}" stroke-width="2"/>`,
  },
  {
    id: "star",
    label: "Star",
    layer: "behind",
    level: 8,
    svg: `<path fill="none" stroke="#ca8a04" stroke-width="3.5" stroke-linecap="round" d="M60 26 L60 10"/><path fill="#facc15" stroke="#a16207" stroke-width="2" stroke-linejoin="round" d="M60.0 -11.0 L62.9 -3.0 L71.4 -2.7 L64.8 2.5 L67.1 10.7 L60.0 6.0 L52.9 10.7 L55.2 2.5 L48.6 -2.7 L57.1 -3.0 Z"/>`,
  },
] as const satisfies readonly Extra[];

/** The level an extra unlocks at: 1 for those open to everyone (and for ids this release lacks). */
export function extraLevel(id: string | null | undefined): number {
  const e: Extra | undefined = EXTRAS.find((x) => x.id === id);
  return e?.level ?? 1;
}

export type ExtraId = (typeof EXTRAS)[number]["id"];

/** Whether `id` names an extra this release knows (the api accepts only these). */
export function isExtraId(id: unknown): id is ExtraId {
  return typeof id === "string" && EXTRAS.some((e) => e.id === id);
}

function extraOf(id: string | null | undefined): Extra | undefined {
  return EXTRAS.find((e) => e.id === id);
}

export interface BeanOptions {
  /** Index into the seat colours. */
  colour: number;
  /** An extra's id. Null, or one this release doesn't know yet, draws a plain bean. */
  extra?: string | null;
  /** Default "ready". */
  mood?: Mood;
  /** The seat number in a corner badge, so colour is never the only cue. */
  badge?: number;
  /** Pixels; omit to size it with CSS. */
  size?: number;
  /** Draw the face and the extra; off for tiny sizes (below about 32 px) where they turn to mush. */
  detail?: boolean;
}

/** One bean as a standalone, decorative SVG element. */
export function beanSvg(o: BeanOptions): string {
  const colour = seatColour(o.colour);
  const mood: Mood = MOODS.includes(o.mood as Mood) ? (o.mood as Mood) : "ready";
  const detail = o.detail ?? true;
  const extra = detail ? extraOf(o.extra) : undefined;
  const size =
    o.size === undefined
      ? ""
      : ` width="${String(Math.round(o.size))}" height="${String(Math.round(o.size))}"`;
  const fade = mood === "reconnecting" ? ` opacity="0.55"` : "";
  const badge =
    o.badge === undefined
      ? ""
      : `<circle cx="98" cy="100" r="19" fill="${INK}" stroke="${colour}" stroke-width="3.5"/><text x="98" y="107.5" fill="#edf0f6" font-family="Unbounded, system-ui, sans-serif" font-weight="700" font-size="21" text-anchor="middle">${String(Math.trunc(o.badge))}</text>`;
  return [
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 120 120"${size} aria-hidden="true" focusable="false" overflow="visible">`,
    extra?.layer === "behind" ? extra.svg : "",
    `<path fill="${colour}"${fade} d="${BEAN_PATH}"/>`,
    `<ellipse cx="42" cy="44" rx="6" ry="11" transform="rotate(28 42 44)" fill="#ffffff" opacity="0.35"/>`,
    detail ? FACES[mood] : "",
    extra?.layer === "front" ? extra.svg : "",
    badge,
    "</svg>",
  ].join("");
}

/** The mark: two beans leaning in, the second mirrored so their dents face, centred in the box. */
export const POD = [
  "translate(40 55.1) rotate(12) scale(0.62) translate(-60 -60)",
  "translate(80 55.1) rotate(-12) scale(-0.62 0.62) translate(-60 -60)",
] as const;

/** The pod's bounds in that box, for a mark without margins. */
export const POD_VIEWBOX = "15 31 90 58";

/**
 * An icon tile: the pod on the dark ground. `rounded` for the browser tab's rounded square;
 * home-screen icons are full-bleed because the phone rounds them. `faces` from 48 px up; `zoom`
 * shrinks the pod into an Android adaptive icon's safe circle.
 */
export function iconSvg({
  rounded,
  faces = false,
  zoom = 1,
}: {
  rounded: boolean;
  faces?: boolean;
  zoom?: number;
}): string {
  const ground = `<rect width="120" height="120"${rounded ? ' rx="26"' : ""} fill="${INK}"/>`;
  const beans = POD.map(
    (transform, i) =>
      `<g transform="${transform}"><path fill="${seatColour(i)}" d="${BEAN_PATH}"/>${faces ? FACES.ready : ""}</g>`,
  ).join("");
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 120 120">${ground}<g transform="translate(60 60) scale(${String(zoom)}) translate(-60 -60)">${beans}</g></svg>\n`;
}
